/* eslint-disable no-param-reassign */
import { createSlice } from '@reduxjs/toolkit';
import {
    exportAdHocBookings,
    exportDailyAccessList,
    fetchAdHocBookings,
    fetchDailyAccessList,
    reportRequestKey,
} from './dailyReportsApi';
import { DailyReportsState } from './types';

const initialState: DailyReportsState = {
    dalRows: [],
    dalDate: null,
    dalEndDate: null,
    adHocAm: [],
    adHocPm: [],
    retailAdHocAm: [],
    retailAdHocPm: [],
    adHocDate: null,
    adHocEndDate: null,
    requestedDalDate: null,
    requestedAdHocDate: null,
    isFetchingDal: false,
    isFetchingAdHoc: false,
    isExporting: false,
    error: null,
};

export const dailyReportsSlice = createSlice({
    name: 'dailyReports',
    initialState,
    reducers: {
        clearDailyReports: (state) => {
            state.dalRows = [];
            state.dalDate = null;
            state.dalEndDate = null;
            state.adHocAm = [];
            state.adHocPm = [];
            state.retailAdHocAm = [];
            state.retailAdHocPm = [];
            state.adHocDate = null;
            state.adHocEndDate = null;
            state.error = null;
        },
    },
    extraReducers: (builder) => {
        // Daily Access List. Rapid date changes can resolve out of order, so we
        // track the latest requested range and ignore any response that isn't
        // for it (both the data and the spinner/error state).
        builder.addCase(fetchDailyAccessList.pending, (state, action) => {
            state.isFetchingDal = true;
            state.error = null;
            state.requestedDalDate = reportRequestKey(action.meta.arg);
        });
        builder.addCase(fetchDailyAccessList.fulfilled, (state, action) => {
            // Guard on the range WE requested (meta.arg), not the one the API
            // echoes back, so a bad server date can't cause valid data to drop.
            if (reportRequestKey(action.meta.arg) !== state.requestedDalDate) {
                return;
            }
            state.isFetchingDal = false;
            state.dalDate = action.payload.date;
            state.dalEndDate = action.payload.endDate;
            state.dalRows = action.payload.rows;
        });
        builder.addCase(fetchDailyAccessList.rejected, (state, action) => {
            if (reportRequestKey(action.meta.arg) !== state.requestedDalDate) {
                return;
            }
            state.isFetchingDal = false;
            state.error = action.payload ?? 'Failed to load the daily access list.';
        });

        // Ad Hoc bookings — same stale-response guard as the DAL, keyed on the
        // whole range so switching between a day and a range can't cross wires.
        builder.addCase(fetchAdHocBookings.pending, (state, action) => {
            state.isFetchingAdHoc = true;
            state.error = null;
            state.requestedAdHocDate = reportRequestKey(action.meta.arg);
        });
        builder.addCase(fetchAdHocBookings.fulfilled, (state, action) => {
            // Guard on the requested range (meta.arg), see fetchDailyAccessList.
            if (reportRequestKey(action.meta.arg) !== state.requestedAdHocDate) {
                return;
            }
            state.isFetchingAdHoc = false;
            state.adHocDate = action.payload.date;
            state.adHocEndDate = action.payload.endDate;
            state.adHocAm = action.payload.am;
            state.adHocPm = action.payload.pm;
            state.retailAdHocAm = action.payload.retailAm;
            state.retailAdHocPm = action.payload.retailPm;
        });
        builder.addCase(fetchAdHocBookings.rejected, (state, action) => {
            if (reportRequestKey(action.meta.arg) !== state.requestedAdHocDate) {
                return;
            }
            state.isFetchingAdHoc = false;
            state.error = action.payload ?? 'Failed to load the ad hoc bookings.';
        });

        builder.addCase(exportDailyAccessList.pending, (state) => {
            state.isExporting = true;
        });
        builder.addCase(exportDailyAccessList.fulfilled, (state) => {
            state.isExporting = false;
        });
        builder.addCase(exportDailyAccessList.rejected, (state) => {
            // Export failures surface via a notification (see the thunk); don't
            // set the shared `error`, which drives the in-modal "couldn't load"
            // alert for fetch failures only.
            state.isExporting = false;
        });

        builder.addCase(exportAdHocBookings.pending, (state) => {
            state.isExporting = true;
        });
        builder.addCase(exportAdHocBookings.fulfilled, (state) => {
            state.isExporting = false;
        });
        builder.addCase(exportAdHocBookings.rejected, (state) => {
            // See exportDailyAccessList.rejected — notification only, no shared error.
            state.isExporting = false;
        });
    },
});

export const { clearDailyReports } = dailyReportsSlice.actions;
export default dailyReportsSlice.reducer;
